import Vue from 'vue';
import Router, { Route } from 'vue-router';
import Start from './app/views/Start.vue';
import { appConfig, isDevelopment } from './config/app.config';
import { Routes } from './app/constants/routes';
import { i18n } from '@/plugins/i18n.plugin';
import { sendPageChangeEvent, sendPageInformationEvent } from '@/app/utils/google-analytics-utils';
import { constructionUtil } from '@/app/utils/construction-utils';

Vue.use(Router);

let baseUrlPath = {};
if (appConfig.env !== 'magnolia') {
    baseUrlPath = {
        base: appConfig.baseUrl,
    };
}

const GUI_URL_BASE_PATH = '/page/internal';

export const router = new Router({
    mode: 'history',
    ...baseUrlPath,
    scrollBehavior: () => {
        return { x: 0, y: 0 };
    },
    routes: [
        {
            path: '/',
            redirect: `${GUI_URL_BASE_PATH}/start`,
        },
        {
            path: `${GUI_URL_BASE_PATH}/success`,
            name: Routes.Success,
            component: () => import('./app/views/Success.vue'),
        },
        {
            path: `${GUI_URL_BASE_PATH}/overview`,
            name: Routes.Overview,
            component: () => import('./app/views/Overview.vue'),
        },
        {
            path: `${GUI_URL_BASE_PATH}/start`,
            component: Start,
            children: [
                {
                    path: '',
                    redirect: `${GUI_URL_BASE_PATH}/start/1`,
                },
                {
                    path: '1',
                    name: Routes.ConstructionTypesStep,
                    component: () => import('./app/views/start/TypeStep.vue'),
                },
                {
                    path: '2',
                    name: Routes.WhereStep,
                    component: () => import('./app/views/start/WhereStep.vue'),
                },
                {
                    path: '3',
                    name: Routes.WhenStep,
                    component: () => import('./app/views/start/WhenStep.vue'),
                },
                {
                    path: '4',
                    name: Routes.SumStep,
                    component: () => import('./app/views/start/SumStep.vue'),
                },
                {
                    path: '5',
                    name: Routes.RisksStep,
                    component: () => import('./app/views/start/RiskStep.vue'),
                },
                {
                    path: '6',
                    name: Routes.MatrixStep,
                    component: () => import('./app/views/start/MatrixStep.vue'),
                },
                {
                    path: '7',
                    name: Routes.ContactStep,
                    component: () => import('./app/views/start/ContactStep.vue'),
                },
            ],
        },
        {
            path: `${GUI_URL_BASE_PATH}/summary`,
            name: Routes.SummaryStep,
            component: () => import('./app/views/Summary.vue'),
        },
        {
            path: `${GUI_URL_BASE_PATH}/edit`,
            component: () => import('./app/views/Edit.vue'),
            children: [
                {
                    path: 'contact',
                    name: Routes.ContactEdit,
                    component: () => import('./app/views/edit/ContactEdit.vue'),
                },
                {
                    path: 'construction',
                    name: Routes.ConstructionEdit,
                    component: () => import('./app/views/edit/ConstructionEdit.vue'),
                },
                {
                    path: 'risk',
                    name: Routes.RiskEdit,
                    component: () => import('./app/views/edit/RiskEdit.vue'),
                },
                {
                    path: 'matrix',
                    name: Routes.MatrixEdit,
                    component: () => import('./app/views/edit/MatrixEdit.vue'),
                },
            ],
        },
        {
            path: '*',
            redirect: `${GUI_URL_BASE_PATH}/start`,
        },
    ],
});

router.beforeEach((to, from, next) => {
    if (from.path !== to.path || from.path === '/') {
        sendPageInformationEvent(to.query.lang as string, to.query.referrerportal as string, to);
        setTimeout(() => {
            sendPageChangeEvent(to);
        }, 500);
    }
    if (!hasQueryParams(to) && hasQueryParams(from)) {
        if (from.path === to.path) {
            next(false);
        } else {
            const galstackFromUrl = constructionUtil.getStackFromUrl(location.href);
            if (galstackFromUrl && isDevelopment()) {
                next({
                    name: to.name,
                    query: {
                        ...to.query,
                        lang: to.query.lang ? to.query.lang : i18n.locale,
                        galstack: galstackFromUrl,
                    },
                });
            } else {
                next({
                    name: to.name,
                    query: {
                        ...to.query,
                        lang: to.query.lang ? to.query.lang : i18n.locale,
                    },
                });
            }

        }
    } else {
        next();
    }
});

function hasQueryParams(route: Route) {
    return !!Object.keys(route.query).length;
}
